import { motion } from "framer-motion";
import { Crown } from "lucide-react";
import { Link } from "react-router-dom";

export const inputCls =
  "w-full rounded-md border border-[#27272A] bg-[#050505] px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-300 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/40";

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-zinc-600">{hint}</span>}
    </label>
  );
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        data-testid="modal"
        className={`relative my-auto w-full ${wide ? "max-w-3xl" : "max-w-lg"} border border-[#27272A] bg-[#0A0A0A] p-6 sm:p-8`}
      >
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-serif text-xl font-semibold">{title}</h3>
          <button
            onClick={onClose}
            data-testid="modal-close-button"
            className="text-zinc-500 transition-colors hover:text-white"
          >
            ✕
          </button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

export function GoldButton({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-black transition-transform duration-300 hover:scale-[1.03] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 ${className}`}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:border-[#D4AF37]/60 hover:text-[#D4AF37] disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}

export function ScoreBadge({ score }) {
  if (score == null) return <span className="text-xs text-zinc-600">—</span>;
  const color =
    score >= 75 ? "text-[#D4AF37] border-[#D4AF37]/50 bg-[#D4AF37]/10"
    : score >= 50 ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
    : "text-zinc-400 border-white/15 bg-white/5";
  return (
    <span data-testid="score-badge" className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold ${color}`}>
      {score}
    </span>
  );
}

export const STAGE_LABELS = {
  DISCOVERED: "Discovered", QUALIFIED: "Qualified", RESEARCHED: "Researched",
  VALUE_IDENTIFIED: "Value Identified", ENGAGED: "Engaged", CONTACTED: "Contacted",
  RESPONDED: "Conversation", PARTNERSHIP_PROPOSED: "Proposal", NEGOTIATION: "Negotiation",
  CONFIRMED: "Confirmed", SCHEDULED: "Scheduled", DELIVERED: "Delivered",
  AUDIENCE_EXPOSURE: "Audience Exposure", LEADS: "Leads", SALES: "Revenue",
};

export const PIPELINE_STAGES = Object.keys(STAGE_LABELS);

export function StageBadge({ stage }) {
  return (
    <span data-testid="stage-badge" className="inline-flex items-center whitespace-nowrap rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-300">
      {STAGE_LABELS[stage] || stage}
    </span>
  );
}

export const PLATFORM_META = {
  podcast: { label: "Podcast", color: "text-amber-400" },
  youtube: { label: "YouTube", color: "text-red-400" },
  facebook: { label: "Facebook", color: "text-blue-400" },
  tiktok: { label: "TikTok", color: "text-pink-400" },
  instagram: { label: "Instagram", color: "text-fuchsia-400" },
  other: { label: "Other", color: "text-zinc-400" },
};

export function PlatformTag({ platform }) {
  const meta = PLATFORM_META[platform] || PLATFORM_META.other;
  return <span className={`text-xs font-semibold uppercase tracking-wider ${meta.color}`}>{meta.label}</span>;
}

export function EmptyState({ icon: Icon, title, desc, action }) {
  return (
    <div className="flex flex-col items-center justify-center border border-dashed border-[#27272A] px-8 py-16 text-center">
      {Icon && <Icon className="h-8 w-8 text-zinc-600" strokeWidth={1.5} />}
      <p className="mt-4 font-serif text-lg font-semibold text-zinc-300">{title}</p>
      <p className="mt-1 max-w-sm text-sm text-zinc-500">{desc}</p>
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function PageHeader({ title, sub, children }) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight">{title}</h1>
        {sub && <p className="mt-1 text-sm text-zinc-500">{sub}</p>}
      </div>
      {children && <div className="flex items-center gap-3">{children}</div>}
    </div>
  );
}

export function Spinner({ label = "Loading" }) {
  return (
    <div className="flex h-full min-h-[40vh] items-center justify-center">
      <div className="flex items-center gap-3 text-sm text-zinc-500">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
        {label}…
      </div>
    </div>
  );
}

export function BrandMark({ small }) {
  return (
    <Link to="/app" className="flex items-center gap-3">
      <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10">
        <Crown className="h-3.5 w-3.5 text-[#D4AF37]" strokeWidth={1.5} />
      </span>
      {!small && (
        <span className="leading-tight">
          <span className="block font-serif text-base font-semibold tracking-wide">KINGDOM</span>
          <span className="block text-[9px] font-medium uppercase tracking-[0.26em] text-zinc-500">
            Partnership Blueprint
          </span>
        </span>
      )}
    </Link>
  );
}
